// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { Link, Stack, Typography, Accordion, AccordionSummary, AccordionDetails, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation, useNavigate } from 'react-router-dom';

type INavItem = {
  text: string;
  url: string;
  children?: Array<INavItem>;
};

type NavigationItemProps = {
  Icon: React.ComponentType;
  title: string;
  url: string;
  navItems?: Array<INavItem>;
}

const adjustPathname = (p: string) => p.endsWith('/') ? p.slice(0, -1) : p;

const ChildNavItem = ({ navItem }: { navItem: INavItem }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const adjustedPathname = adjustPathname(location.pathname);

  const isSubExpandedByUrl =
    navItem.children?.some(
      (gc) =>
        adjustedPathname === `/${gc.url}` ||
        adjustedPathname.endsWith(`/${gc.url}`)
    ) ?? adjustedPathname.includes(navItem.url);

  const [open, setOpen] = useState(isSubExpandedByUrl);

  useEffect(() => {
    if (isSubExpandedByUrl) setOpen(true);
  }, [isSubExpandedByUrl]);

  return (
    <Accordion
      disableGutters
      expanded={open}
      onChange={(_, v) => setOpen(v)}
      sx={{
        backgroundColor: 'transparent',
        boxShadow: 'none',
        border: 0,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon
            sx={{
              fontSize: 15,
              color: isSubExpandedByUrl ? '#0c9fdc' : '#374151',
            }}
          />
        }
        sx={{
          px: 1,
          py: 0,
          minHeight: 36,
          borderRadius: '4px',
          bgcolor: isSubExpandedByUrl ? '#e8f6fd' : 'transparent',
          '&:hover': { bgcolor: '#f0f9fe' },
          transition: 'background-color 0.2s',
          '& .MuiAccordionSummary-content': { margin: '0' },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: isSubExpandedByUrl ? '#0c9fdc' : '#9CA3AF',
              flexShrink: 0,
              transition: 'background-color 0.2s',
            }}
          />
          <Typography
            variant="body2"
            fontWeight={isSubExpandedByUrl ? 700 : 500}
            sx={{
              color: isSubExpandedByUrl ? '#0c9fdc' : '#374151',
              transition: 'color 0.2s',
              userSelect: 'none',
            }}
          >
            {navItem.text}
          </Typography>
        </Stack>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          p: 0,
          mt: 0.25,
          ml: 1.5,
          pl: 1.5,
          borderLeft: '2px solid #F3F4F6',
        }}
      >
        {navItem.children.map((grandChild) => {
          const isGrandActive =
            adjustedPathname === `/${grandChild.url}` ||
            adjustedPathname.endsWith(`/${grandChild.url}`);
          const handleGrandClick = (event) => {
            if (event.button === 1) {
              window.open(`/${grandChild.url}`, '_blank');
            } else {
              navigate(`/${grandChild.url}`, { state: {} });
            }
          };
          return (
            <a
              key={grandChild.url}
              onAuxClick={handleGrandClick}
              onClick={handleGrandClick}
              style={{ textDecoration: 'none', cursor: 'pointer' }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  px: 1,
                  py: 0.75,
                  borderRadius: '4px',
                  bgcolor: isGrandActive ? '#e8f6fd' : 'transparent',
                  '&:hover': { bgcolor: '#f0f9fe' },
                  transition: 'background-color 0.15s',
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    bgcolor: isGrandActive ? '#0c9fdc' : '#9CA3AF',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="h6"
                  fontWeight={isGrandActive ? 700 : 500}
                  sx={{
                    color: isGrandActive ? '#0c9fdc' : '#374151',
                    transition: 'color 0.15s',
                    userSelect: 'none',
                  }}
                >
                  {grandChild.text}
                </Typography>
              </Stack>
            </a>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
};

export const NavigationItem = ({ navItems, title, url, Icon }: NavigationItemProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isExpandedByUrl = location.pathname.includes(url);
  const [open, setOpen] = useState(isExpandedByUrl);

  useEffect(() => {
    if (isExpandedByUrl) setOpen(true);
  }, [isExpandedByUrl]);

  const handleMouseMiddleClick = (event) => {
    if (event.button === 1) {
      window.open(`/${url}`, '_blank');
    }
  };

  const handleClickSingle = () => {
    navigate(`/${url}`, { state: {} });
  };

  const adjustedPathname = adjustPathname(location.pathname);

  return (
    <>
      {navItems?.length ? (
        <Accordion
          disableGutters
          expanded={open}
          onChange={(_, v) => setOpen(v)}
          sx={{
            backgroundColor: 'transparent',
            boxShadow: 'none',
            border: 0,
            '&:before': { display: 'none' },
            mx: 1,
            mb: 0.5,
          }}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  color: isExpandedByUrl ? 'white' : '#374151',
                  transition: 'color 0.2s',
                }}
              />
            }
            sx={{
              px: 1.5,
              py: 0,
              minHeight: 44,
              borderRadius: '6px',
              bgcolor: isExpandedByUrl ? '#0c9fdc' : 'transparent',
              '&:hover': {
                bgcolor: isExpandedByUrl ? '#0c9fdc' : '#f0f9fe',
              },
              transition: 'background-color 0.2s',
              '& .MuiAccordionSummary-content': { margin: '0' },
            }}
          >
            <Stack spacing={1.5} alignItems="center" direction="row">
              <Icon
                sx={{
                  fontSize: 20,
                  color: isExpandedByUrl ? 'white' : '#374151',
                  transition: 'color 0.2s',
                }}
              />
              <Typography
                variant="body2"
                fontWeight={isExpandedByUrl ? 700 : 500}
                sx={{
                  color: isExpandedByUrl ? 'white' : '#374151',
                  transition: 'color 0.2s',
                  userSelect: 'none',
                }}
              >
                {title}
              </Typography>
            </Stack>
          </AccordionSummary>

          <AccordionDetails
            sx={{
              p: 0,
              mt: 0.5,
              ml: 2.5,
              pl: 1.5,
              borderLeft: '2px solid #E5E7EB',
            }}
          >
            {navItems.map((navItem) => {
              if (navItem.children?.length) {
                return <ChildNavItem key={navItem.url} navItem={navItem} />;
              }

              const isActive = adjustedPathname
                .split('/')
                .includes(navItem?.url.split('/')[1]);

              const handleClick = (event) => {
                if (event.button === 1) {
                  window.open(`/${navItem.url}`, '_blank');
                } else {
                  navigate(`/${navItem.url}`, { state: {} });
                }
              };

              return (
                <a
                  key={navItem.url}
                  onAuxClick={handleClick}
                  onClick={handleClick}
                  style={{ textDecoration: 'none', cursor: 'pointer' }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      px: 1,
                      py: 0.75,
                      borderRadius: '4px',
                      bgcolor: isActive ? '#e8f6fd' : 'transparent',
                      '&:hover': { bgcolor: '#f0f9fe' },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: isActive ? '#0c9fdc' : '#9CA3AF',
                        flexShrink: 0,
                        transition: 'background-color 0.2s',
                      }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={isActive ? 700 : 500}
                      sx={{
                        color: isActive ? '#0c9fdc' : '#374151',
                        transition: 'color 0.15s',
                        userSelect: 'none',
                      }}
                    >
                      {navItem.text}
                    </Typography>
                  </Stack>
                </a>
              );
            })}
          </AccordionDetails>
        </Accordion>
      ) : (
        <Box onAuxClick={handleMouseMiddleClick} sx={{ display: 'block', mx: 1, mb: 0.5 }}>
          <Stack
            spacing={1.5}
            alignItems="center"
            direction="row"
            px={1.5}
            sx={{
              borderRadius: '6px',
              height: 44,
              bgcolor: isExpandedByUrl ? '#0c9fdc' : 'transparent',
              '&:hover': { bgcolor: isExpandedByUrl ? '#0c9fdc' : '#f0f9fe' },
              transition: 'background-color 0.2s',
              cursor: 'pointer',
            }}
            onClick={handleClickSingle}
          >
            <Icon
              sx={{
                fontSize: 20,
                color: isExpandedByUrl ? 'white' : '#374151',
                transition: 'color 0.2s',
              }}
            />
            <Typography
              variant="body2"
              fontWeight={isExpandedByUrl ? 700 : 500}
              sx={{
                color: isExpandedByUrl ? 'white' : '#374151',
                transition: 'color 0.2s',
                userSelect: 'none',
              }}
            >
              {title}
            </Typography>
          </Stack>
        </Box>
      )}
    </>
  );
};
